/* eslint-disable @typescript-eslint/no-unused-vars */

/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */
import { useQuery } from '@apollo/client';
import {
  CssBaseline,
  Grid,
  StyledEngineProvider,
  ThemeProvider as MuiThemeProvider,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import * as Sentry from '@sentry/react';
import { PermissionEnum } from '@thro-types/access-handler';
import { ShibUser, User } from '@thro-types/users';

import { setDefaultOptions } from 'date-fns';
import { enUS } from 'date-fns/locale';
import de from 'date-fns/locale/de';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import AUTHORS_QUERY from 'gql/queries/PermissionProvider/getAuthors.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';
import USER_PERMISSION_QUERY from 'gql/queries/userPermission.gql';

import { useSelfEntitlementGroupsQuery } from 'api/hooks/users';
import 'styles/global-styles.scss';
import theme from 'styles/theme/theme';
import { AuthProvider } from 'utils/context/AuthProvider';
import { LayoutProvider } from 'utils/context/LayoutContext';
import Roles from 'utils/enums/Roles';
import { FEATURE_FLAGS } from 'utils/featureFlags';
import { hasAtLeastOneActivityLogPermission, isAreaAdmin } from 'utils/permissionHelper';

import ErrorBoundary from './components/ErrorBoundary';
import { ErrorFallback } from './components/ErrorBoundary/components/ErrorFallback';
import GQLGuard from './components/GQLGuard';
import { NotFoundPage } from './components/NotFoundPage/Loadable';
import DevStatusDialog from './components/Util/DevStatusDialog';
import PrivateRoute from './components/Util/PrivateRoute';
import { SystemNotificationDialog } from './components/Util/SystemNotification';
import {
  ActivityLog,
  Alert,
  Articles,
  Awpm,
  Billing,
  Bulletin,
  Calendar,
  Chat,
  Companies,
  CoursesListFrame,
  Events,
  EventsListFrame,
  FormError,
  Fwpm,
  HomePage,
  Io,
  Jobs,
  LeihBar,
  LoginLayout,
  Profile,
  Settings,
  ShibUserDetail,
  ShibUserList,
  TestUserList,
  Watchlist
} from './containers/';
import { NewsletterDialog } from './containers/Jobmarket/CompanyDetailPage/components/NewsletterDialog';
import { Polyas } from './containers/Polyas';

export function App() {
  const { i18n } = useTranslation();
  const token = localStorage.getItem('jwt-token'); //TODO currently when token is present but expired the queries are being sent but get a 401
  // Just preload the query for later use via cache
  const userQuery = useQuery<any, any>(USER_QUERY, {
    context: { clientName: 'federation' },
    skip: !token
  });
  const userPermissionQuery = useQuery<any, any>(USER_PERMISSION_QUERY, {
    context: { clientName: 'federation' },
    skip: !token
  });
  const authorRolesQuery = useQuery(AUTHORS_QUERY, {
    skip: !token
  });
  const localizedMuiTheme = useMemo(() => theme(i18n.language), [i18n.language]);

  setDefaultOptions({ locale: i18n.language === 'de' ? de : enUS, weekStartsOn: 1 });

  const { isLoading: myEntitlementGroupsLoading } = useSelfEntitlementGroupsQuery();

  const [isLoadingFinished, setIsLoadingFinished] = useState(false);
  const [showNewsletterDialog, setShowNewsletterDialog] = useState<boolean>(false);

  const isFeedbackEnabled = useBooleanFlagValue(
    FEATURE_FLAGS.GLOBAL.SENTRYFEEDBACKBUTTON_ENABLED,
    false
  );

  useEffect(() => {
    if (
      !userQuery.loading &&
      !userPermissionQuery.loading &&
      !authorRolesQuery.loading &&
      !myEntitlementGroupsLoading
    ) {
      setIsLoadingFinished(true);
      if (isFeedbackEnabled && userQuery.data) Sentry.getFeedback()?.createWidget();
    }
  }, [
    authorRolesQuery.loading,
    userPermissionQuery.loading,
    userQuery,
    myEntitlementGroupsLoading,
    isFeedbackEnabled
  ]);

  useEffect(() => {
    if (
      isLoadingFinished &&
      userQuery.data?.currentUser.company &&
      userQuery.data?.currentUser.company.agreedToNewsletter === null
    ) {
      setShowNewsletterDialog(true);
    }
  }, [userQuery.data?.currentUser, isLoadingFinished]);

  // if (userQuery.error) {
  //   return (
  //     <div
  //       style={{
  //         height: '100vh',
  //         display: 'flex',
  //         alignItems: 'center',
  //         justifyContent: 'center'
  //       }}
  //     >
  //       <ErrorFallback error={userQuery.error} componentStack={null} />
  //     </div>
  //   );
  // }

  return (
    <MuiThemeProvider theme={localizedMuiTheme}>
      {!isLoadingFinished ? (
        <Backdrop open sx={{ backgroundColor: theme('de').palette.primary.light, zIndex: 2000 }}>
          <Grid container justifyContent="center">
            <CircularProgress color="primary" />
          </Grid>
        </Backdrop>
      ) : (
        <AuthProvider
          userData={userQuery.data}
          authorRolesData={authorRolesQuery.data}
          permissionsData={userPermissionQuery.data}
        >
          <StyledEngineProvider injectFirst>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <ThemeProvider theme={localizedMuiTheme}>
                <ErrorBoundary boundaryTag="appBoundary">
                  <Helmet titleTemplate="%s - TH Rosenheim Community" defaultTitle="Home">
                    <meta name="description" content="TH Rosenheim Community" />
                  </Helmet>
                  <Alert />
                  <FormError />
                  {showNewsletterDialog && (
                    <NewsletterDialog
                      dialogOpen
                      setShowNewsletterDialog={setShowNewsletterDialog}
                    />
                  )}
                  <GQLGuard query={[userQuery, userPermissionQuery]} ignoreErrors hideLoader>
                    {([userData, userPermissionData]) => {
                      const currentUser: User = userData?.currentUser;
                      const userPermission: PermissionEnum[] =
                        userPermissionData?.getUserPermission;
                      Sentry.setUser(currentUser);
                      return (
                        <LayoutProvider>
                          <CssBaseline />
                          <DevStatusDialog />
                          {currentUser && <SystemNotificationDialog />}
                          <Routes>
                            <Route path="/frame/io/courses" element={<CoursesListFrame />} />
                            <Route path="/frame/io/events" element={<EventsListFrame />} />
                            <Route
                              index
                              element={
                                <PrivateRoute user={currentUser} notAnonymous>
                                  <HomePage />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/login"
                              element={
                                <PrivateRoute onlyAnonymous user={currentUser}>
                                  <LoginLayout variant="login" query={userQuery} />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/login/success/"
                              element={
                                <PrivateRoute onlyAnonymous user={currentUser}>
                                  <LoginLayout variant="confirm" query={userQuery} />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/forgot"
                              element={
                                <PrivateRoute onlyAnonymous user={currentUser}>
                                  <LoginLayout variant="forgot" />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/users/access/:token"
                              element={
                                <PrivateRoute onlyAnonymous user={currentUser}>
                                  <LoginLayout variant="confirm" />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/register"
                              element={<Navigate to={{ pathname: '/login' }} />}
                            />
                            <Route
                              path="/profile"
                              element={
                                <PrivateRoute
                                  requires={{
                                    roles: [
                                      Roles.STUDENT,
                                      Roles.EMPLOYEE,
                                      Roles.HIWI,
                                      Roles.LECTURER,
                                      Roles.PROFESSOR
                                    ]
                                  }}
                                  user={currentUser}
                                >
                                  <Profile />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/calendar"
                              element={
                                <PrivateRoute
                                  requires={{
                                    roles: [
                                      Roles.STUDENT,
                                      Roles.EMPLOYEE,
                                      Roles.HIWI,
                                      Roles.LECTURER,
                                      Roles.PROFESSOR
                                    ],
                                    entitlements: [Roles.JOBMARKET_ADMIN]
                                  }}
                                  user={currentUser}
                                >
                                  <Calendar />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/shib/users"
                              element={
                                <PrivateRoute
                                  requires={{
                                    entitlements: [Roles.SUPER_USER],
                                    roles: [
                                      Roles.EMPLOYEE,
                                      Roles.HIWI,
                                      Roles.LECTURER,
                                      Roles.PROFESSOR
                                    ]
                                  }}
                                  user={currentUser}
                                >
                                  <ShibUserList />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/shib/users/:id"
                              element={
                                <PrivateRoute
                                  requires={{
                                    entitlements: [Roles.SUPER_USER],
                                    roles: [
                                      Roles.EMPLOYEE,
                                      Roles.HIWI,
                                      Roles.LECTURER,
                                      Roles.PROFESSOR
                                    ]
                                  }}
                                  user={currentUser}
                                >
                                  <ShibUserDetail />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/test/users"
                              element={
                                <PrivateRoute
                                  requires={{
                                    entitlements: [Roles.SUPER_USER]
                                  }}
                                  user={currentUser}
                                >
                                  <TestUserList />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/activity-log/*"
                              element={
                                <PrivateRoute
                                  canAccess={
                                    isAreaAdmin(currentUser, userPermission) &&
                                    hasAtLeastOneActivityLogPermission(userPermission)
                                  }
                                  user={currentUser}
                                >
                                  <ActivityLog />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/chat/*"
                              element={
                                <Chat
                                  currentUser={currentUser as ShibUser}
                                  userPermission={userPermission}
                                />
                              }
                            />
                            <Route
                              path="/articles/*"
                              element={<Articles currentUser={currentUser} />}
                            />
                            <Route
                              path="/companies/*"
                              element={<Companies currentUser={currentUser} />}
                            />
                            <Route
                              path="/bulletin/*"
                              element={<Bulletin currentUser={currentUser} />}
                            />
                            <Route path="/jobs/*" element={<Jobs currentUser={currentUser} />} />
                            <Route path="/awpm/*" element={<Awpm currentUser={currentUser} />} />
                            <Route path="/fwpm/*" element={<Fwpm currentUser={currentUser} />} />
                            <Route path="/io/*" element={<Io currentUser={currentUser} />} />
                            <Route
                              path="/settings/*"
                              element={<Settings currentUser={currentUser} />}
                            />
                            <Route
                              path="/events/*"
                              element={
                                <Events currentUser={currentUser} userPermission={userPermission} />
                              }
                            />
                            <Route
                              path="/leihbar/*"
                              element={
                                <LeihBar
                                  currentUser={currentUser}
                                  userPermission={userPermission}
                                />
                              }
                            />
                            <Route
                              path="/watchlist/*"
                              element={<Watchlist currentUser={currentUser} />}
                            />
                            <Route
                              path="/billing/*"
                              element={
                                <Billing
                                  currentUser={currentUser}
                                  userPermission={userPermission}
                                />
                              }
                            />
                            <Route
                              path="/election/*"
                              element={
                                <Polyas currentUser={currentUser} userPermission={userPermission} />
                              }
                            />
                            {/* //? Sometimes you get redirected to this after logout. So to prevent that, we redirect to /login */}
                            <Route path="Shibboleth.sso/*" element={<Navigate to="/login" />} />
                            <Route path="*" element={<NotFoundPage />} />
                          </Routes>
                        </LayoutProvider>
                      );
                    }}
                  </GQLGuard>
                </ErrorBoundary>
              </ThemeProvider>
            </LocalizationProvider>
          </StyledEngineProvider>
        </AuthProvider>
      )}
    </MuiThemeProvider>
  );
}

export default App;
